import { DateTime } from 'luxon'
import { standardLabraDateFormat } from '@labrav/common'

export type RenewalDetailsLike = {
  renewalPricingTerms?: string | null
  renewalPricingTerm?: string | null
  renewalCountType?: string | null
  renewalCount?: number | null
  numberOfRenewals?: number | null
  priceUpliftType?: string | null
  priceUpliftPercent?: number | null
  priceUpliftMinPercent?: number | null
  priceUpliftMaxPercent?: number | null
  optOutDeadlineDays?: number | null
  adjustmentDeadlineDays?: number | null
  defaultUpliftPercent?: number | null
  sellerOptOut?: boolean | null
  buyerOptOut?: boolean | null
  autoRenew?: boolean | null
  enteredUpliftPercent?: number | null
  subscriptionEndDate?: string | null
  contractEndDate?: string | null
  expirationDate?: string | null
  agreementStartDate?: string | null
  serviceLength?: number | null
}

const ordinalDay = (n: number) => {
  const suffixes = ['th', 'st', 'nd', 'rd'] as const
  const v = n % 100
  return `${n}${suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]}`
}

export const getRenewalDeadlineDateTime = (
  days: number | null | undefined,
  endIso: string | undefined,
  timeZone: string
) => {
  if (days == null || Number.isNaN(Number(days)) || !endIso) return null
  const date = DateTime.fromISO(endIso, { zone: timeZone }).minus({
    days: Number(days),
  })
  return date.isValid ? date : null
}

export const formatRenewalBannerDate = (date: DateTime) =>
  `${ordinalDay(date.day)} ${date.toFormat('LLL, yyyy')}`

export type RenewalBanner =
  | { kind: 'updateUplift'; dateLabel: string }
  | { kind: 'optOutPassed' }
  | { kind: 'optOutUpcoming'; dateLabel: string }

export const resolveRenewalBanner = (
  item: RenewalDetailsLike | null | undefined,
  endIso: string | undefined,
  timeZone: string,
  nowIso?: string
): RenewalBanner | null => {
  if (!shouldShowRenewalDetailsFromTerms(item) || !item) return null
  const now = (
    nowIso
      ? DateTime.fromISO(nowIso, { zone: timeZone })
      : DateTime.now().setZone(timeZone)
  ).startOf('day')
  if (!now.isValid) return null

  const optOut = getRenewalDeadlineDateTime(
    item.optOutDeadlineDays,
    endIso,
    timeZone
  )
  const adjustment = getRenewalDeadlineDateTime(
    item.adjustmentDeadlineDays,
    endIso,
    timeZone
  )
  const isRange =
    resolveRenewalPricingTerms(item) === 'PRICE_UPLIFT' &&
    (item.priceUpliftType ?? 'FIXED') === 'RANGE'

  if (
    isRange &&
    item.enteredUpliftPercent == null &&
    adjustment &&
    now <= adjustment.startOf('day')
  ) {
    return {
      kind: 'updateUplift',
      dateLabel: formatRenewalBannerDate(adjustment),
    }
  }
  if (optOut) {
    if (now > optOut.startOf('day')) return { kind: 'optOutPassed' }
    return {
      kind: 'optOutUpcoming',
      dateLabel: formatRenewalBannerDate(optOut),
    }
  }
  return null
}

export const formatRenewalOptOut = (value?: boolean | null) => {
  if (value === true) return 'Yes'
  if (value === false) return 'No'
  return '-'
}

export const formatRenewalsAllowed = (item: RenewalDetailsLike) => {
  if (item.renewalCountType === 'INDEFINITE') return 'Indefinite'
  if (item.renewalCount != null) return String(item.renewalCount)
  if (item.numberOfRenewals != null) return String(item.numberOfRenewals)
  return '-'
}

export const formatPriceUpliftYesNo = (item: RenewalDetailsLike) => {
  const terms = resolveRenewalPricingTerms(item)
  if (terms === 'PRICE_UPLIFT') return 'Yes'
  if (terms === 'NO_PRICE_UPLIFT') return 'No'
  return '-'
}

export const formatUpliftPercent = (item: RenewalDetailsLike) => {
  if (resolveRenewalPricingTerms(item) !== 'PRICE_UPLIFT') return '-'
  if ((item.priceUpliftType ?? 'FIXED') === 'RANGE') {
    const min = item.priceUpliftMinPercent
    const max = item.priceUpliftMaxPercent
    if (min == null || max == null) return '-'
    return `${min}% - ${max}%`
  }
  if (item.priceUpliftPercent == null) return '-'
  return `${item.priceUpliftPercent}%`
}

/** Figma shows absolute dates; form stores days-before-end. */
export const formatRenewalDeadlineDate = (
  days: number | null | undefined,
  endIso: string | undefined,
  timeZone: string
) => {
  if (days == null || Number.isNaN(Number(days))) return '-'
  if (!endIso) return `${days} days`
  const date = DateTime.fromISO(endIso, { zone: timeZone }).minus({
    days: Number(days),
  })
  if (!date.isValid) return `${days} days`
  return date.toFormat(standardLabraDateFormat)
}

export const resolveRenewalPricingTerms = (
  item?: RenewalDetailsLike | null
) => {
  if (!item) return ''
  const raw = item.renewalPricingTerms ?? item.renewalPricingTerm
  return typeof raw === 'string' ? raw.toUpperCase() : ''
}

export const shouldShowRenewalDetailsFromTerms = (
  item?: RenewalDetailsLike | null
) => {
  const terms = resolveRenewalPricingTerms(item)
  return terms === 'NO_PRICE_UPLIFT' || terms === 'PRICE_UPLIFT'
}

export type ContractEndLike = {
  subscriptionEndDate?: string | null
  contractEndDate?: string | null
  expirationDate?: string | null
  agreementStartDate?: string | null
  serviceLength?: number | null
}

export const getContractEndIso = (
  offer: ContractEndLike
): string | undefined => {
  if (offer.subscriptionEndDate) return offer.subscriptionEndDate
  if (offer.contractEndDate) return offer.contractEndDate
  if (offer.expirationDate) return offer.expirationDate
  if (offer.agreementStartDate && offer.serviceLength != null) {
    return (
      DateTime.fromISO(offer.agreementStartDate)
        .plus({ months: Number(offer.serviceLength) })
        .toISODate() || undefined
    )
  }
  return undefined
}

export const parsePercentInput = (raw: string) => {
  const cleaned = raw.replace(/%/g, '').trim()
  if (cleaned === '') return null
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}
